import { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Loader from "../UI/Loader";
import Alert from "../UI/Alert";
import {
  getVideoTutorialInicioQuizService,
  putVideoTutorialInicioQuizFileService,
  putVideoTutorialInicioQuizUrlService,
} from "../../services/quiz/quiz.service.js";

const normalizeVideoUrl = (raw) => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
};

// Detectar si es YouTube
const isYoutubeUrl = (url) =>
  url?.includes("youtube.com") || url?.includes("youtu.be");

// Convertir URL normal a embed
const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;

  const match =
    url.match(/v=([^&]+)/) ||
    url.match(/youtu\.be\/([^?]+)/);

  const videoId = match?.[1];
  if (!videoId) return null;

  return `https://www.youtube.com/embed/${videoId}`;
};

const VideoTutorialInicioQuiz = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [urlValue, setUrlValue] = useState("");
  const [currentVideoUrl, setCurrentVideoUrl] = useState(null);

  const hasFile = Boolean(selectedFile);
  const hasUrl = Boolean(urlValue.trim());

  // =========================
  // Cargar video actual
  // =========================
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        setLoading(true);
        setError(null);

        const data = await getVideoTutorialInicioQuizService();
        const rawUrl =
          data?.video_tutorial_url || data?.video_url || null;

        setCurrentVideoUrl(normalizeVideoUrl(rawUrl));
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data?.detail ||
            "Error al cargar el video actual"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVideo();
  }, []);

  // Limpieza preview
  useEffect(() => {
    return () => {
      if (filePreviewUrl) {
        URL.revokeObjectURL(filePreviewUrl);
      }
    };
  }, [filePreviewUrl]);

  // =========================
  // Handlers
  // =========================
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setError(null);
    setSuccess(null);

    if (!file) return;

    if (hasUrl) setUrlValue("");

    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setSelectedFile(file);
    setFilePreviewUrl(preview);
  };

  const handleUrlChange = (e) => {
    const value = e.target.value;
    setError(null);
    setSuccess(null);

    if (hasFile) {
      setSelectedFile(null);
      if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }

    setUrlValue(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!hasFile && !hasUrl) {
      return setError("Debes seleccionar un archivo o ingresar una URL.");
    }

    if (hasFile && hasUrl) {
      return setError("Solo puedes elegir una opción: archivo o enlace.");
    }

    try {
      setSaving(true);

      let data;

      if (hasFile) {
        data = await putVideoTutorialInicioQuizFileService(selectedFile);
      } else {
        data = await putVideoTutorialInicioQuizUrlService(
          urlValue.trim()
        );
      }

      const rawUrl =
        data?.video_tutorial_url || data?.video_url || null;

      setCurrentVideoUrl(normalizeVideoUrl(rawUrl));

      setSelectedFile(null);
      setFilePreviewUrl(null);
      setUrlValue("");

      setSuccess("Video tutorial actualizado correctamente.");
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.detail ||
          "Error al guardar el video tutorial"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-3">
        <Loader center text="Cargando video tutorial..." delay={200} />
      </div>
    );
  }

  const previewSource = filePreviewUrl || currentVideoUrl;
  const youtubeEmbed = isYoutubeUrl(previewSource)
    ? getYoutubeEmbedUrl(previewSource)
    : null;

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert
          message={error}
          variant="error"
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          message={success}
          variant="success"
          onClose={() => setSuccess(null)}
        />
      )}

      <div className="row g-3">
        <div className="col-12 col-md-6">
          <label className="form-label fw-medium d-flex align-items-center gap-2">
            <Icon icon="mdi:upload" width={20} />
            Subir archivo de video
          </label>
          <input
            type="file"
            accept="video/*"
            className="form-control"
            onChange={handleFileChange}
            disabled={saving || hasUrl}
          />
        </div>

        <div className="col-12 col-md-6">
          <label className="form-label fw-medium d-flex align-items-center gap-2">
            <Icon icon="mdi:link-variant" width={20} />
            URL externa del video
          </label>
          <input
            type="url"
            className="form-control"
            placeholder="https://..."
            value={urlValue}
            onChange={handleUrlChange}
            disabled={saving || hasFile}
          />
        </div>
      </div>

      <p className="text-muted small mt-3">
        Solo puedes elegir una opción: archivo o enlace.
      </p>

      {previewSource && (
        <div className="mt-3">
          <label className="form-label fw-medium d-block">
            Vista previa
          </label>

          <div className="d-flex justify-content-center">
            <div
              className="ratio ratio-16x9 border rounded overflow-hidden"
              style={{ maxWidth: "480px", width: "100%" }}
            >
              {youtubeEmbed ? (
                <iframe
                  src={youtubeEmbed}
                  title="Video tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ width: "100%", height: "100%", border: 0 }}
                />
              ) : (
                <video
                  src={previewSource}
                  controls
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 d-flex justify-content-end">
        <button
          type="submit"
          className="btn btn-primary d-flex align-items-center gap-2"
          disabled={saving}
        >
          {saving ? (
            <>
              <Loader type="spinner" size="sm" delay={0} />
              Guardando...
            </>
          ) : (
            <>
              <Icon icon="mdi:content-save" width={20} />
              Guardar video
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default VideoTutorialInicioQuiz;